/**
 * Per-entity display objects — one `EntityView` per `EntityId`.
 *
 * The stage keeps a `Map<EntityId, EntityView>` and reconciles it
 * against each interpolated frame in `renderFrame`. Views are pure
 * display sacks; they hold no gameplay state.
 */

import { Container, Graphics, Sprite, Text, Texture } from "pixi.js";
import type { ChampionId, WireSimState } from "@pit/protocol";
import {
  BODY_MAGNIFICATION,
  CHARACTER_TARGET_HEIGHT_UNITS,
  DEFAULT_BODY_RADIUS_UNITS,
  WORLD_TEXT_RESOLUTION,
  facingToScreenVec,
  projectHeight,
  projectRadius,
  renderWorldToScreen,
  type ProjectionCenter,
} from "./projection.js";
import {
  BAR_BORDER,
  BODY_OUTLINE,
  CAST_BAR,
  ENEMY_PROJECTILE_COLOR,
  FACING_TICK,
  HP_BAR_BG,
  HP_BAR_COLOR,
  OWN_PROJECTILE_COLOR,
  WALL_COLOR,
  teamColor,
} from "./colors.js";
import type {
  InterpolatedEntity,
  InterpolatedProjectile,
  InterpolatedState,
  InterpolatedWall,
} from "./interpolate.js";
import type { SpriteSheetMeta } from "./championSprites.js";
import { entitySpriteFrame } from "./spriteAnimation.js";
import type { Keymap } from "../input/keybinding/index.js";
import {
  buildNameplates,
  nameplateColor,
  type Nameplate,
  type NameplateStyle,
} from "./nameplate.js";
import { resourceCeilings } from "../champions/resourceLabels.js";
import { resourceColor } from "../champions/resourceColors.js";
import {
  DEFAULT_SPELL_NAME_STYLE,
  SPELL_NAME_ROW_H,
  castingSpellLabel,
  type SpellNameStyle,
} from "./spellNameLabel.js";

/** Re-exported from `projection.ts` (its canonical home) so existing importers
 *  of `DEFAULT_BODY_RADIUS_UNITS` from this module keep working. */
export { DEFAULT_BODY_RADIUS_UNITS };

/** Screen-space size of the HP bar (screen px, world-anchored). */
export const HP_BAR_W = 40;
export const HP_BAR_H = 5;
export const CAST_BAR_W = 40;
export const CAST_BAR_H = 4;
/** Screen-space size of the above-head resource bar (#337). Thinner than the
 *  HP bar — a secondary readout, not the primary one. */
export const RESOURCE_BAR_W = 40;
export const RESOURCE_BAR_H = 3;

/** Vertical gap between stacked bars in the above-head stack, screen px. */
export const BAR_STACK_GAP_PX = 2;

/**
 * The above-head stack's BASE, in screen px above the 2D world anchor (≈ the
 * body centre for the procedural body / sprite path) — the BOTTOM edge of the
 * innermost slot (the cast bar), which the whole stack grows upward from.
 *
 * `17` is the clearance the pre-#843 stack left above the body centre (its
 * innermost element was the HP bar, drawn top-edge at 22 with `HP_BAR_H = 5`
 * below it), preserved so the stack sits exactly as far off the 2D body as it
 * always has. The drawn body's radius is `bodyRadiusPx(DEFAULT_BODY_RADIUS_UNITS)`
 * — well inside this, so nothing in the stack overlaps the body (asserted in
 * `entityView.test.ts`).
 */
export const BAR_STACK_BASE_Y = 17;

/**
 * Clearance above the projected head apex before the stack starts, in screen
 * px (#246, re-pointed by #843). This is the gap under the INNERMOST slot (the
 * cast bar since #843), so the whole stack — cast, resource and HP bars alike
 * — clears the model's head rather than only its topmost bar. Mirrors the
 * small gap `BAR_STACK_BASE_Y` leaves above the 2D body's drawn radius.
 */
export const BAR_STACK_HEAD_CLEARANCE_PX = 6;

/**
 * The stack's base above the world anchor for a 3D character (#246): the
 * bottom edge of the innermost slot. The 3D placement root's world anchor is
 * the entity's GROUND point (feet — see `characterModel.ts`/`isoCamera.ts`),
 * not the body centre the 2D circle used, so the stack must clear the model's
 * full projected standing height (`CHARACTER_TARGET_HEIGHT_UNITS`), not the
 * fixed 2D `BAR_STACK_BASE_Y` — else it lands mid-body (waist) for a tall
 * model. Every 3D champion currently normalises to the SAME target height
 * (`characterModel.ts` has no per-champion override), so this is one static,
 * body-derived number, not a per-instance measurement.
 */
export const BAR_STACK_BASE_Y_3D =
  projectHeight(CHARACTER_TARGET_HEIGHT_UNITS) + BAR_STACK_HEAD_CLEARANCE_PX;

/**
 * The above-head stack's base offset above an entity's world anchor, in screen
 * px, for its render path (#246): the body-derived head-height base for 3D
 * characters, the fixed 2D base otherwise.
 */
export function barStackBaseY(is3D: boolean): number {
  return is3D ? BAR_STACK_BASE_Y_3D : BAR_STACK_BASE_Y;
}

/**
 * Every slot's vertical offset above the world anchor, in screen px. Each
 * value is the slot's TOP edge (`drawBar` is called with `-offset` and draws
 * DOWNWARD, so a bar at offset `O` occupies `[O - height, O]` above the
 * anchor); a LARGER offset is HIGHER on screen.
 */
export interface BarStackOffsets {
  /** Innermost slot (#843) — closest to the head/body. */
  readonly castBarY: number;
  readonly resourceBarY: number;
  /** Outermost BAR slot (#843) — the HP bar tops the three-bar stack. */
  readonly hpBarY: number;
  /**
   * Top edge of the outermost bar — the anchor everything ABOVE the bars grows
   * upward from (#843).
   */
  readonly stackTopY: number;
  /**
   * BOTTOM edge of the casting-spell name row (#836) — one gap above the
   * stack top. The label is bottom-anchored text (like the nameplate), so
   * this is the value its position is set from directly; the row it occupies
   * is `[spellNameY, spellNameY + SPELL_NAME_ROW_H]`.
   */
  readonly spellNameY: number;
  /**
   * Base the target-hotkey nameplate grows upward from: the TOP of the
   * reserved spell-name row (#836), i.e. the top of the whole above-head
   * stack. Fixed, so the nameplate does not jump when a cast starts/ends.
   */
  readonly nameplateBaseY: number;
}

/**
 * The above-head stack's slot offsets (#843/#836), bottom → top: cast bar →
 * resource bar → HP bar → spell name → nameplate.
 *
 * Slots are FIXED — derived from the base plus every slot below them,
 * regardless of which bars are actually drawn this frame. That is what keeps
 * #337's independent-omission rule true in BOTH directions now that the
 * resource bar sits BETWEEN two other elements: an entity with no resource
 * pool leaves the resource slot EMPTY rather than collapsing it, so neither
 * its neighbour below (cast) nor above (HP) moves. A cumulative walk that only
 * added a term when the resource bar was drawn would drop the HP bar by
 * `RESOURCE_BAR_H + BAR_STACK_GAP_PX` for pool-less champions. Pure.
 *
 * #836's spell-name row obeys the same rule for the same reason, one level
 * up: its `SPELL_NAME_ROW_H` is reserved whether or not a name is drawn this
 * frame (idle unit, instant cast, unresolved champion, unknown slot), so the
 * nameplate above it never jumps as a cast starts or ends. Because every slot
 * is derived from `barStackBaseY(is3D)`, the label lands correctly on BOTH
 * anchor bases (2D ≈ body centre, 3D = the model's ground/feet point) — a
 * hardcoded offset would be wrong on one of them, and wrong for either the
 * pool-having or the pool-less half of the roster.
 */
export function barStackOffsets(is3D: boolean): BarStackOffsets {
  const base = barStackBaseY(is3D);
  const castBarY = base + CAST_BAR_H;
  const resourceBarY = castBarY + BAR_STACK_GAP_PX + RESOURCE_BAR_H;
  const hpBarY = resourceBarY + BAR_STACK_GAP_PX + HP_BAR_H;
  const spellNameY = hpBarY + BAR_STACK_GAP_PX;
  return {
    castBarY,
    resourceBarY,
    hpBarY,
    stackTopY: hpBarY,
    spellNameY,
    nameplateBaseY: spellNameY + SPELL_NAME_ROW_H,
  };
}

/**
 * The HP bar's vertical offset above an entity's world anchor, in screen px —
 * the outermost bar of the stack since #843. Thin accessor over
 * {@link barStackOffsets} for consumers that only care where the health readout
 * lands (e.g. the pillar-occlusion check that the bar clears the 3D model).
 */
export function hpBarOffsetY(is3D: boolean): number {
  return barStackOffsets(is3D).hpBarY;
}

/**
 * Context the render loop feeds `renderFrame` so it can resolve the
 * target-hotkey nameplate (#136) for each entity: the latest wire snapshot
 * (roster membership), the followed seat (viewer perspective), the LIVE keymap
 * (key labels; rebinds refresh immediately), and the visual style. Cosmetic —
 * no sim/hash impact.
 */
export interface NameplateRenderContext {
  readonly snapshot: WireSimState | null;
  /** `null` when the viewer's own seat isn't known yet (#731) — no nameplate
   *  is resolvable relative to an unknown seat, so `buildNameplates` returns
   *  an empty map rather than resolving against seat 0. */
  readonly ownSeat: number | null;
  readonly keymap: Keymap;
  readonly style: NameplateStyle;
}

/** Dead-body alpha (matches WP3 rule — corpses render, don't block). */
export const DEAD_ALPHA = 0.3;

/** Facing tick length in screen px. */
export const FACING_TICK_LENGTH = 10;

/** World-unit movement threshold (position delta) that counts as "walking". */
export const MOVE_EPSILON = 0.25;

/**
 * A champion sprite sheet loaded into a Pixi texture (#223 Task 4). The sheet
 * source is sliced per frame via `entitySpriteFrame`; `meta` describes the
 * layout. `null` at every seat until sprite art is committed → procedural body.
 */
export interface LoadedChampionSheet {
  readonly texture: Texture;
  readonly meta: SpriteSheetMeta;
}

/**
 * Per-frame sprite context threaded through `renderFrame`. `resolve` maps an
 * entity to its loaded sheet (or `null` → procedural body — the rip-out
 * contract); `clockMs` is the render-side animation clock (never a sim tick).
 * `use3D` (optional) marks entities whose body is drawn by the Three character
 * layer (#237) — for those, the 2D body/sprite/facing-tick are suppressed while
 * the HP/cast bars + nameplate still render on top. `championId` (#337)
 * resolves an entity to its `ChampionId` for the above-head resource bar's
 * pool ceiling + color lookup (`resourceLabels.ts`/`resourceColors.ts`) — the
 * SAME seat→champion roster resolution `mount.ts` already threads for sprite/
 * 3D-model selection; omitted (or a seat resolving to `null`) → no resource
 * bar (the champion's pool ceiling can't be resolved). `hidden` (#422,
 * optional) marks entities the LOCAL viewer must not see AT ALL — an
 * enemy-team entity currently holding an unexpired stealth aura
 * (`../input/stealthVisibility.js`'s `isHiddenFromViewer`, computed by
 * `mount.ts` since this file may not import `@pit/protocol` at runtime); when
 * true, sprite/body/facing-tick/bars/nameplate are ALL suppressed for that
 * entity this frame. Omitted → nothing is ever hidden (pre-#422
 * byte-identical).
 */
export interface SpriteRenderContext {
  readonly resolve: (entity: InterpolatedEntity) => LoadedChampionSheet | null;
  readonly clockMs: number;
  readonly use3D?: (entity: InterpolatedEntity) => boolean;
  readonly championId?: (entity: InterpolatedEntity) => ChampionId | null;
  readonly hidden?: (entity: InterpolatedEntity) => boolean;
}

/** A view is a container with named children so update() can find them
 *  without pushing everything into instance fields. */
export interface EntityView {
  readonly root: Container;
  readonly body: Graphics;
  /** Champion sprite (shown when a sheet is loaded; else hidden, body draws). */
  readonly sprite: Sprite;
  readonly facingTick: Graphics;
  readonly hpBar: Graphics;
  /** Above-head resource (rage/mana/energy) bar (#337); hidden when the
   *  entity's champion has no known resource pool. */
  readonly resourceBar: Graphics;
  readonly castBar: Graphics;
  /**
   * Casting-spell name above the HP bar (#836); hidden whenever there is no
   * name to show (not casting / champion unresolved / unknown slot).
   *
   * POOLING: one `Text` per entity view, created once with `createEntityView`
   * and reused for the view's whole life — the view Map is itself the pool
   * (`renderFrame` creates a view when an entity appears and destroys it when
   * the entity leaves), so a 3v3 with all six champions casting allocates at
   * most six `Text` objects EVER, none per frame. `.visible` is toggled and
   * `.text` is only reassigned when the string actually changes (reassigning
   * re-rasterizes the glyphs), which is the same discipline the nameplate
   * uses and the same "never allocate in the 60fps loop" rule
   * `combatTextManager.ts` enforces with its explicit pool.
   */
  readonly spellName: Text;
  /** Target-hotkey label above the HP bar (#136); hidden when unresolved. */
  readonly nameplate: Text;
  /** Last rendered world position — drives the position-delta walk detection. */
  prevPos: { wx: number; wy: number } | null;
}

/**
 * Create a new empty view. Nothing is drawn yet; `updateEntityView`
 * populates it based on the entity's current interpolated state. The
 * nameplate's STATIC style (font/weight/outline) is baked in from `style`
 * here; its per-entity text + color are applied per frame in `updateEntityView`.
 * The spell-name label's style (#836) is baked in the same way — ALL of it,
 * colour included, since unlike the nameplate it has no per-entity variation.
 */
export function createEntityView(
  style: NameplateStyle,
  spellNameStyle: SpellNameStyle = DEFAULT_SPELL_NAME_STYLE,
): EntityView {
  const root = new Container();
  const body = new Graphics();
  const sprite = new Sprite();
  // Bottom-center anchor: the sprite's feet sit at the entity's world position
  // (the footprint), so the ground contact lines up with the collision circle.
  sprite.anchor.set(0.5, 1);
  sprite.visible = false;
  const facingTick = new Graphics();
  const hpBar = new Graphics();
  const resourceBar = new Graphics();
  const castBar = new Graphics();
  const nameplate = new Text({
    text: "",
    style: { fontFamily: "sans-serif" },
    // #730: the nameplate rides inside the zoom-scaled entities layer —
    // supersample so a magnified viewport does not soften it.
    resolution: WORLD_TEXT_RESOLUTION,
  });
  // Bottom-center anchor so the label grows UPWARD from a point just above the
  // HP bar top; position is set per frame in `updateEntityView`.
  nameplate.anchor.set(0.5, 1);
  nameplate.style.fontSize = style.fontSizePx;
  nameplate.style.fontWeight = String(style.fontWeight) as never;
  if (style.outlinePx > 0) {
    nameplate.style.stroke = { color: style.outlineColor, width: style.outlinePx };
  }
  nameplate.visible = false;
  const spellName = new Text({
    text: "",
    style: { fontFamily: "sans-serif" },
    // Same #730 supersampling reason as the nameplate: this rides inside the
    // zoom-scaled entities layer.
    resolution: WORLD_TEXT_RESOLUTION,
  });
  // Bottom-center anchor (like the nameplate): the label grows UPWARD from
  // its reserved row's bottom edge, and is centred on the unit — it may
  // exceed the 40px bar width, which is the deliberate legibility call (see
  // `SpellNameStyle.maxChars`).
  spellName.anchor.set(0.5, 1);
  spellName.style.fontSize = spellNameStyle.fontSizePx;
  spellName.style.fontWeight = String(spellNameStyle.fontWeight) as never;
  spellName.style.fill = spellNameStyle.color;
  if (spellNameStyle.outlinePx > 0) {
    spellName.style.stroke = {
      color: spellNameStyle.outlineColor,
      width: spellNameStyle.outlinePx,
    };
  }
  spellName.visible = false;
  // Draw order: body/sprite (bottom) → facing tick → bars → spell name →
  // nameplate (top).
  root.addChild(sprite);
  root.addChild(body);
  root.addChild(facingTick);
  root.addChild(hpBar);
  root.addChild(resourceBar);
  root.addChild(castBar);
  root.addChild(spellName);
  root.addChild(nameplate);
  return {
    root,
    body,
    sprite,
    facingTick,
    hpBar,
    resourceBar,
    castBar,
    spellName,
    nameplate,
    prevPos: null,
  };
}

/**
 * Screen-space body radius (px) for an entity's LIVE `radius` (#342) — the
 * procedural-body draw's formula, pulled out so it's unit-testable without a
 * Pixi/GL context (mirrors `resourceBarFraction` below). `hitTest.ts` mirrors
 * this SAME formula (its own inline copy — it deliberately does not import
 * this module, which pulls in Pixi; see its doc comment) against each
 * entity's own live `radius`, so the clickable area always matches the drawn
 * area. Floored at 3px so a heavily-zoomed-out or degenerate radius never
 * vanishes entirely.
 */
export function bodyRadiusPx(radiusUnits: number): number {
  const projected = projectRadius(radiusUnits) * BODY_MAGNIFICATION;
  return projected < 3 ? 3 : projected;
}

/**
 * The above-head resource bar's fill fraction (#337), or `null` when the bar
 * should be hidden entirely — `resourceCurrent` is `null` (pool-less wire
 * entity) OR `maxResource` is `null`/`≤ 0` (champion has no known ceiling,
 * see `resourceLabels.ts`). Clamped to `[0, 1]` (a stale/over-cap wire value
 * never overdraws the bar). Pure — no Pixi, unit-tested directly.
 */
export function resourceBarFraction(
  resourceCurrent: number | null,
  maxResource: number | null,
): number | null {
  if (resourceCurrent === null || maxResource === null || maxResource <= 0) return null;
  const f = resourceCurrent / maxResource;
  return f < 0 ? 0 : f > 1 ? 1 : f;
}

/**
 * Update view geometry to reflect `entity` in world state.
 *
 *   • Position: worldToScreen(pos, center).
 *   • Body: filled circle in team color, projectRadius(radiusUnits).
 *   • Facing tick: short line from center in facing direction.
 *   The above-head stack, bottom → top (#843): cast bar → resource bar → HP
 *   bar → nameplate. Every bar sits at its FIXED slot from `barStackOffsets`
 *   (#337 independent omission: an undrawn resource bar leaves its slot empty
 *   and moves NEITHER neighbour), and the whole stack clears the 2D body /
 *   projected 3D head — the base, not the topmost bar, is what carries the
 *   clearance (#246, re-pointed by #843: anchor ≈ body centre in 2D, the
 *   model's GROUND/feet point in 3D per `characterModel.ts`/`isoCamera.ts`, so
 *   a much taller silhouette needs a much bigger base).
 *
 *   • HP bar: 40×5, the TOP bar of the stack, ALWAYS the same green (#344 —
 *     the fraction is already conveyed by bar WIDTH; a fraction-tinted color
 *     was redundant and, once the resource bar landed alongside it, made a
 *     low-resource bar misread as a low-health one).
 *   • Resource bar (#337): 40×3, directly under the HP bar, shown ONLY when
 *     `championId` resolves to a champion with a known resource-pool ceiling
 *     (`resourceLabels.ts`) AND the wire `entity.resource` is non-null —
 *     omitted with NO layout shift otherwise (mirrors `FrameResourceBar`'s
 *     independent-omission rule). Colored by the pool's content `resourceId`
 *     (#344 — `resourceColors.ts`: rage red / energy yellow / mana blue /
 *     unmapped light-purple placeholder).
 *   • Cast bar: 40×4, the BOTTOM bar of the stack (under the resource slot),
 *     only while casting.
 *   • Spell name (#836): the CASTING ability's name, in the reserved row above
 *     the HP bar, resolved from `cast.slot` + `championId` client-side (never
 *     from the wire). One lifetime with the cast bar — `cast === null` (idle,
 *     or an instant, which never appears in a snapshot as a cast) draws
 *     nothing, and so does an unresolved name; the row stays reserved either
 *     way, so nothing above or below it shifts.
 *   • Nameplate: target-hotkey letter above the spell-name row (#136), when
 *     resolved.
 *   • Dead: 30% alpha, no bars, no spell name, no nameplate.
 *   • Hidden (#422): an enemy-team entity currently holding an unexpired
 *     stealth aura is suppressed ENTIRELY (sprite/body/facing-tick/bars/
 *     nameplate) for this viewer — genuinely invisible, not merely
 *     unclickable. Checked before every other draw branch.
 */
export function updateEntityView(
  view: EntityView,
  entity: InterpolatedEntity,
  center: ProjectionCenter,
  frame: InterpolatedState,
  nameplate: Nameplate | null,
  style: NameplateStyle,
  sheet: LoadedChampionSheet | null,
  clockMs: number,
  is3D: boolean,
  /** #337: this entity's champion id (for the resource bar's pool ceiling +
   *  color), or `null` when unresolved (no bar). */
  championId: ChampionId | null = null,
  /** #422: true when the LOCAL viewer must not see this entity at all (an
   *  enemy-team entity currently stealthed). Default `false` — pre-#422
   *  byte-identical when omitted. */
  hiddenFromViewer = false,
  /** #836: casting-spell name treatment. Defaults to the shipped style;
   *  `mountRenderer` threads a themed one through `renderFrame`. */
  spellNameStyle: SpellNameStyle = DEFAULT_SPELL_NAME_STYLE,
): void {
  const p = renderWorldToScreen(entity.pos, center);
  view.root.position.set(p.sx, p.sy);

  // ── Movement (position delta since last frame → walk animation) ──
  const moving =
    view.prevPos !== null &&
    (Math.abs(entity.pos.wx - view.prevPos.wx) > MOVE_EPSILON ||
      Math.abs(entity.pos.wy - view.prevPos.wy) > MOVE_EPSILON);
  view.prevPos = { wx: entity.pos.wx, wy: entity.pos.wy };

  // ── #422: hidden-from-viewer — suppress EVERYTHING and stop. Position +
  //    prevPos bookkeeping above still ran (so un-hiding later doesn't
  //    misreport a teleport as a walk-cycle), but nothing is drawn/visible. ──
  if (hiddenFromViewer) {
    view.root.visible = false;
    return;
  }
  view.root.visible = true;

  if (is3D) {
    // ── 3D character (#237): the Three layer draws the body + orientation, so
    //    the 2D body/sprite/facing-tick stay hidden; bars + nameplate below
    //    still render on top of the 3D model. The on-ground equivalents of the
    //    two readouts cleared here (collision circle + facing tick) are the
    //    Three layer's ground marker — `three3d/groundMarker.ts` (#825). ──
    view.sprite.visible = false;
    view.body.clear();
    view.facingTick.clear();
  } else if (sheet !== null) {
    // ── Champion sprite: encodes facing (row) + animation (state). The sprite
    //    conveys orientation, so the procedural body + facing tick stay hidden. ──
    drawEntitySprite(view, sheet, entity, moving, clockMs);
    view.body.clear();
    view.facingTick.clear();
  } else {
    // ── Procedural body (rip-out fallback: no sheet committed/loaded) ──
    view.sprite.visible = false;
    view.body.clear();
    const rPx = bodyRadiusPx(entity.radius);
    // (#342: the entity's LIVE radius — a `statMod` on `"radius"` (e.g. Stand
    // Behind Me's +30%) changes this per-tick, so the drawn body scales with
    // it instead of the fixed `DEFAULT_BODY_RADIUS_UNITS` default.
    // BODY_MAGNIFICATION (#277): now 1 — the drawn circle is the true
    // collision radius, floored at 3px so it never vanishes at extreme
    // zoom-out. hitTest.ts reads the SAME per-entity radius so the clickable
    // area always matches the drawn area. WP7's real sprites replace this and
    // pick their own scale.)
    view.body
      .circle(0, 0, rPx)
      .fill({ color: teamColor(entity.team) })
      .stroke({ width: 1, color: BODY_OUTLINE });

    // ── Facing tick ──
    view.facingTick.clear();
    const v = facingToScreenVec(entity.facing);
    view.facingTick
      .moveTo(0, 0)
      .lineTo(v.sdx * FACING_TICK_LENGTH, v.sdy * FACING_TICK_LENGTH)
      .stroke({ width: 2, color: FACING_TICK });
  }

  // ── Dead: dim everything, skip bars + nameplate ──
  if (!entity.alive) {
    view.root.alpha = DEAD_ALPHA;
    view.hpBar.clear();
    view.resourceBar.clear();
    view.castBar.clear();
    view.spellName.visible = false;
    view.nameplate.visible = false;
    return;
  }
  view.root.alpha = 1;

  // ── Above-head stack (#843): fixed slots, bottom → top cast/resource/HP. ──
  const stack = barStackOffsets(is3D);

  // ── HP bar — the stack's TOP bar (#344: ALWAYS green — see the doc comment
  //    above). ──
  const hpFraction = entity.maxHp <= 0 ? 0 : entity.hp / entity.maxHp;
  drawBar(view.hpBar, -HP_BAR_W / 2, -stack.hpBarY, HP_BAR_W, HP_BAR_H, hpFraction, HP_BAR_COLOR);

  // ── Resource bar (#337) — shown only when this champion has a known pool
  //    AND the wire carries a live balance; otherwise its slot is simply left
  //    EMPTY (independent omission — the cast bar below and the HP bar above
  //    keep their own fixed offsets, no layout shift in either direction). ──
  const ceilings = championId === null ? null : resourceCeilings(championId);
  const resourceFraction = resourceBarFraction(entity.resource, ceilings?.maxResource ?? null);
  if (resourceFraction === null) {
    view.resourceBar.clear();
  } else {
    drawBar(
      view.resourceBar,
      -RESOURCE_BAR_W / 2,
      -stack.resourceBarY,
      RESOURCE_BAR_W,
      RESOURCE_BAR_H,
      resourceFraction,
      resourceColor(ceilings?.resourceId ?? null),
    );
  }

  // ── Cast bar — the stack's BOTTOM bar (only when casting) ──
  view.castBar.clear();
  const cast = entity.cast;
  if (cast !== null) {
    const start = cast.startTick;
    const end = cast.endTick;
    const total = end - start;
    let fraction = 0;
    if (total > 0) {
      const elapsed = frame.renderTick - start;
      fraction = elapsed < 0 ? 0 : elapsed > total ? 1 : elapsed / total;
    }
    drawBar(
      view.castBar,
      -CAST_BAR_W / 2,
      -stack.castBarY,
      CAST_BAR_W,
      CAST_BAR_H,
      fraction,
      CAST_BAR,
    );
  }

  // ── Casting-spell name (#836) — the reserved row above the HP bar ──
  //    One lifetime with the cast bar above: the SAME `cast` object gates
  //    both, so the label can never linger a frame past the cast (and an
  //    instant, which never appears as a non-null `cast` in a snapshot, never
  //    flashes one). A `null` label draws NOTHING — no placeholder text, no
  //    empty box — and shifts nothing, because the row is reserved anyway.
  const spellNameText = castingSpellLabel(
    championId,
    cast,
    entity.auras,
    // `renderTick` is a float render-time coordinate; the aura-conditional
    // name lookup (#76) is a sim-tick comparison, so floor it to the tick
    // actually being displayed rather than letting a sub-tick fraction flip
    // the label a frame early.
    Math.floor(frame.renderTick),
    spellNameStyle,
  );
  if (spellNameText === null) {
    view.spellName.visible = false;
  } else {
    // Only reassign on change — setting `.text` re-rasterizes the glyphs, and
    // a cast holds the same name for its whole duration.
    if (view.spellName.text !== spellNameText) view.spellName.text = spellNameText;
    view.spellName.position.set(0, -stack.spellNameY);
    view.spellName.visible = true;
  }

  // ── Target-hotkey nameplate (#136) — above the top of the stack ──
  if (nameplate === null) {
    view.nameplate.visible = false;
  } else {
    // An entity's faction (→ color) is fixed for the match, so text + fill only
    // need re-applying when the LABEL changes (rebind / roster shift), which
    // avoids re-rasterizing the glyph every frame.
    if (view.nameplate.text !== nameplate.label) {
      view.nameplate.text = nameplate.label;
      view.nameplate.style.fill = nameplateColor(nameplate.faction, style);
    }
    // Bottom-anchored `offsetAbovePx` above the stack top (screen −y = up) —
    // the top of #836's reserved spell-name row, which sits one gap above the
    // HP bar. Reserved whether or not a name is drawn, so the letter never
    // jumps when a cast starts.
    view.nameplate.position.set(0, -(stack.nameplateBaseY + style.offsetAbovePx));
    view.nameplate.visible = true;
  }
}

/**
 * Reconcile the entity Map against the frame — create missing, update
 * existing, remove absent. Depth-sort by world `y` (#730). Pure over the
 * inputs modulo the Map + Pixi mutation.
 */
export function renderFrame(
  entities: Container,
  views: Map<string, EntityView>,
  frame: InterpolatedState,
  center: ProjectionCenter,
  nameplateCtx: NameplateRenderContext,
  spriteCtx: SpriteRenderContext,
  /** #836: casting-spell name treatment, baked into each view on creation and
   *  applied per frame. Defaults to the shipped style. */
  spellNameStyle: SpellNameStyle = DEFAULT_SPELL_NAME_STYLE,
): void {
  // Resolve each entity's target-hotkey nameplate once per frame (#136) from
  // the live keymap + roster; looked up per entity below.
  const nameplates = buildNameplates(
    nameplateCtx.snapshot,
    nameplateCtx.ownSeat,
    nameplateCtx.keymap,
  );
  const seen = new Set<string>();
  for (const e of frame.entities) {
    const id = e.entityId;
    seen.add(id);
    let view = views.get(id);
    if (view === undefined) {
      view = createEntityView(nameplateCtx.style, spellNameStyle);
      views.set(id, view);
      entities.addChild(view.root);
    }
    updateEntityView(
      view,
      e,
      center,
      frame,
      nameplates.get(id) ?? null,
      nameplateCtx.style,
      spriteCtx.resolve(e),
      spriteCtx.clockMs,
      spriteCtx.use3D?.(e) ?? false,
      spriteCtx.championId?.(e) ?? null,
      spriteCtx.hidden?.(e) ?? false,
      spellNameStyle,
    );
  }
  // Remove views for entities no longer in state.
  for (const [id, view] of views) {
    if (!seen.has(id)) {
      entities.removeChild(view.root);
      view.root.destroy({ children: true });
      views.delete(id);
    }
  }
  // Depth-sort by world `y` (#730 — the screen-depth axis under the
  // axis-aligned basis; was `x + y` while the basis was rotated 45°). This is
  // the SAME key `arena.pillarDepthKey` sorts pillars by and `hitTest`
  // resolves overlaps by — the three must never diverge or things draw in
  // front of what they are behind. Views hold no world coord after
  // positioning — but we can pull it back from the entity list, since it's
  // cheap.
  const zByRoot = new Map<Container, number>();
  for (const e of frame.entities) {
    const view = views.get(e.entityId);
    if (view !== undefined) {
      zByRoot.set(view.root, e.pos.wy);
    }
  }
  entities.children.sort((a, b) => {
    const az = zByRoot.get(a) ?? 0;
    const bz = zByRoot.get(b) ?? 0;
    return az - bz;
  });
}

// ─── Projectiles (#29) ───────────────────────────────────────────────────────

/** A projectile view: a single small filled rectangle on the fx layer. */
export interface ProjectileView {
  readonly root: Graphics;
}

/** Minimum drawn projectile half-extent in screen px (so tiny projectiles read). */
export const PROJECTILE_MIN_RADIUS_PX = 4;

/**
 * Neutral projectile fill — plain white. Used only when the viewer's own
 * team isn't resolved yet (#422's `viewerTeam === null` window, e.g. very
 * early in a match before the viewer's own entity has appeared) — fail
 * open to the old neutral look rather than guessing a side.
 */
export const PROJECTILE_COLOR = 0xffffff;

/** Length‑to‑width ratio of the drawn projectile rectangle (a little spear). */
export const PROJECTILE_LENGTH_RATIO = 2.4;

/**
 * Viewer-relative projectile fill (#467): own-team projectiles (relative to
 * `viewerTeam`) render blue-tinted, enemy projectiles render red-tinted,
 * regardless of which team INDEX the viewer is on. Falls back to the
 * neutral `PROJECTILE_COLOR` when `viewerTeam` isn't resolved yet.
 */
export function projectileColor(ownerTeam: number, viewerTeam: number | null): number {
  if (viewerTeam === null) return PROJECTILE_COLOR;
  return ownerTeam === viewerTeam ? OWN_PROJECTILE_COLOR : ENEMY_PROJECTILE_COLOR;
}

/**
 * Reconcile projectile views against the frame — create missing, update
 * existing, remove vanished (hit/expired). Drawn on the `fx` layer as small
 * rectangles oriented along travel direction (a little spear), tinted
 * relative to `viewerTeam` (#467: blue = own team, red = enemy, white =
 * viewer team unresolved) so a projectile reads as an in‑flight thrown
 * weapon that's either friendly or hostile at a glance.
 */
export function renderProjectiles(
  fx: Container,
  views: Map<string, ProjectileView>,
  frame: InterpolatedState,
  center: ProjectionCenter,
  viewerTeam: number | null,
): void {
  const seen = new Set<string>();
  for (const p of frame.projectiles) {
    const id = p.projectileId;
    seen.add(id);
    let view = views.get(id);
    if (view === undefined) {
      view = { root: new Graphics() };
      views.set(id, view);
      fx.addChild(view.root);
    }
    updateProjectileView(view, p, center, viewerTeam);
  }
  for (const [id, view] of views) {
    if (!seen.has(id)) {
      fx.removeChild(view.root);
      view.root.destroy();
      views.delete(id);
    }
  }
}

function updateProjectileView(
  view: ProjectileView,
  proj: InterpolatedProjectile,
  center: ProjectionCenter,
  viewerTeam: number | null,
): void {
  const p = renderWorldToScreen(proj.pos, center);
  view.root.position.set(p.sx, p.sy);

  // Half‑width from the projectile radius; length is a small multiple so it
  // reads as a little spear rather than a dot.
  const halfW = Math.max(
    PROJECTILE_MIN_RADIUS_PX,
    projectRadius(proj.radius) * BODY_MAGNIFICATION,
  );
  const halfL = halfW * PROJECTILE_LENGTH_RATIO;

  // Orient along the projected travel direction. Project the velocity vector
  // into screen space by projecting a nearby point along it and taking the
  // delta, so the rectangle points where the spear is actually flying.
  const tip = renderWorldToScreen(
    { wx: proj.pos.wx + proj.dx, wy: proj.pos.wy + proj.dy },
    center,
  );
  const vx = tip.sx - p.sx;
  const vy = tip.sy - p.sy;
  view.root.rotation = vx === 0 && vy === 0 ? 0 : Math.atan2(vy, vx);

  view.root
    .clear()
    .rect(-halfL, -halfW, halfL * 2, halfW * 2)
    .fill({ color: projectileColor(proj.ownerTeam, viewerTeam) })
    .stroke({ width: 1, color: BODY_OUTLINE });
}

// ─── Dynamic-terrain walls (#488, consuming #129's `spawnWall` subsystem) ────

/** A wall view: one Graphics drawing every sub-obstacle circle of the wall. */
export interface WallView {
  readonly root: Graphics;
}

/**
 * Reconcile wall views against the frame — create missing, update existing
 * (position never changes for a live wall, but redrawing is cheap and keeps
 * this the same reconcile-every-frame shape as `renderProjectiles`), remove
 * vanished (expired) walls. Drawn on the `entities` layer as placeholder
 * white boxes — one filled circle per row-of-circles sub-obstacle (#488's
 * scope: no art pass, just a visible/removable placeholder obstacle).
 */
export function renderWalls(
  layer: Container,
  views: Map<string, WallView>,
  frame: InterpolatedState,
  center: ProjectionCenter,
): void {
  const seen = new Set<string>();
  for (const w of frame.walls) {
    const id = w.wallId;
    seen.add(id);
    let view = views.get(id);
    if (view === undefined) {
      view = { root: new Graphics() };
      views.set(id, view);
      layer.addChild(view.root);
    }
    updateWallView(view, w, center);
  }
  for (const [id, view] of views) {
    if (!seen.has(id)) {
      layer.removeChild(view.root);
      view.root.destroy();
      views.delete(id);
    }
  }
}

function updateWallView(view: WallView, wall: InterpolatedWall, center: ProjectionCenter): void {
  view.root.clear();
  for (const o of wall.obstacles) {
    const p = renderWorldToScreen(o.pos, center);
    const radiusPx = projectRadius(o.radius) * BODY_MAGNIFICATION;
    view.root
      .circle(p.sx, p.sy, radiusPx)
      .fill({ color: WALL_COLOR })
      .stroke({ width: 1, color: BODY_OUTLINE });
  }
}

/**
 * Draw the champion sprite frame for `entity`: pick the animation (alive /
 * casting / moving), the facing row, and the current frame, slice the sheet, and
 * scale it so the sprite's footprint matches the collision circle
 * (`projectRadius(radius) × BODY_MAGNIFICATION` — the same footprint the
 * procedural body uses). Presentation-only; runs only when a sheet is loaded.
 */
function drawEntitySprite(
  view: EntityView,
  sheet: LoadedChampionSheet,
  entity: InterpolatedEntity,
  moving: boolean,
  clockMs: number,
): void {
  const f = entitySpriteFrame(
    sheet.meta,
    { alive: entity.alive, casting: entity.cast !== null, moving },
    entity.facing,
    clockMs,
  );
  // Bind (once) a croppable texture over the sheet source, then move its frame
  // rectangle in place (Pixi v8 `Texture.frame` is read-only — mutate, not reassign).
  let tex = view.sprite.texture;
  if (tex.source !== sheet.texture.source) {
    tex = new Texture({ source: sheet.texture.source });
    view.sprite.texture = tex;
  }
  tex.frame.x = f.sx;
  tex.frame.y = f.sy;
  tex.frame.width = f.sw;
  tex.frame.height = f.sh;
  tex.updateUvs();

  // Scale so the sheet footprint (px) maps to the client footprint (px). #342:
  // driven by the entity's LIVE radius, not the fixed default, so a
  // size-changing statMod (Stand Behind Me) visibly scales the sprite too.
  const clientFootprintPx = projectRadius(entity.radius) * BODY_MAGNIFICATION;
  const scale =
    sheet.meta.footprintRadiusPx > 0 ? clientFootprintPx / sheet.meta.footprintRadiusPx : 1;
  view.sprite.scale.set(scale);
  view.sprite.visible = true;
}

function drawBar(
  g: Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  fraction: number,
  fillColor: number,
): void {
  const f = fraction < 0 ? 0 : fraction > 1 ? 1 : fraction;
  g.clear();
  // Background.
  g.rect(x, y, w, h).fill({ color: HP_BAR_BG });
  // Fill.
  if (f > 0) {
    g.rect(x, y, w * f, h).fill({ color: fillColor });
  }
  // Border.
  g.rect(x, y, w, h).stroke({ width: 1, color: BAR_BORDER });
}
